import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Suppliers from './pages/Suppliers';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Clients from './pages/Clients';
import ClientPassport from './pages/ClientPassport';
import Inventory from './pages/Inventory';
import SAV from './pages/SAV';
import SAVTicketDetail from './pages/SAVTicketDetail';
import SupplierDetail from './pages/SupplierDetail';
import Loans from './pages/Loans';
import Prospecting from './pages/Prospecting';
import UserManagement from './pages/UserManagement';
import Partners from './pages/Partners';
import Marketing from './pages/Marketing';
import ExploitationDashboard from './pages/ExploitationDashboard';
import ExploitationClients from './pages/ExploitationClients';
import ExploitationClientDetail from './pages/ExploitationClientDetail';
import ExploitationSiteDetail from './pages/ExploitationSiteDetail';
import ExploitationSiteSettings from './pages/ExploitationSiteSettings';
import ConfigurateurPage from './pages/ConfigurateurPage';
import ConfigurateurAdminPage from './pages/ConfigurateurAdminPage';
import ConfigurateurProfil from './pages/ConfigurateurProfil';
import Comparateur from './pages/Comparateur';

// Components
import Sidebar from './components/Sidebar';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#141617] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img
            src="https://www.riding-watt.com/wp-content/uploads/2024/01/logo-ridingwatt-v2.png"
            alt="Riding Watt"
            className="h-12 animate-pulse"
          />
          <div className="text-[#8C8F94]">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Layout with Sidebar
const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#141617] flex">
      <Sidebar />
      <main className="flex-1 min-h-screen overflow-x-hidden">
        {children}
      </main>
    </div>
  );
};

// Placeholder pages for routes not yet implemented
const PlaceholderPage = ({ title }) => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-4">{title}</h1>
    <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-8 text-center">
      <p className="text-[#8C8F94]">Cette fonctionnalité sera bientôt disponible</p>
    </div>
  </div>
);

const Emails = () => <PlaceholderPage title="Communications Gmail" />;

const Settings = () => {
  const { api, isSuperAdmin } = useAuth();
  const [syncing, setSyncing] = React.useState(false);
  const [syncResult, setSyncResult] = React.useState(null);
  const [axonautStatus, setAxonautStatus] = React.useState(null);
  const [invoices, setInvoices] = React.useState([]);
  const [loadingStatus, setLoadingStatus] = React.useState(true);
  const [gmailConnected, setGmailConnected] = React.useState(false);
  const [gmailLoading, setGmailLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchStatus = async () => {
      try {
        const [statusRes, invoicesRes, gmailRes] = await Promise.all([
          api.get('/axonaut/status'),
          api.get('/axonaut/invoices?payment_status=unpaid').catch(() => ({ data: [] })),
          api.get('/gmail/status').catch(() => ({ data: { connected: false } }))
        ]);
        setAxonautStatus(statusRes.data);
        setInvoices(invoicesRes.data || []);
        setGmailConnected(gmailRes.data?.connected || false);
      } catch (e) {
        console.error('Failed to fetch status:', e);
      } finally {
        setLoadingStatus(false);
        setGmailLoading(false);
      }
    };
    fetchStatus();
  }, [api]);

  const handleGmailConnect = async () => {
    try {
      const res = await api.get('/oauth/gmail/login');
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (e) {
      console.error('Gmail OAuth error:', e);
      alert('Erreur: ' + (e.response?.data?.detail || 'Impossible de se connecter a Gmail'));
    }
  };

  const handleGmailDisconnect = async () => {
    if (!window.confirm('Déconnecter Gmail ?')) return;
    try {
      await api.post('/gmail/disconnect');
      setGmailConnected(false);
    } catch (e) {
      console.error('Gmail disconnect error:', e);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await api.post('/axonaut/sync');
      if (response.data.status === 'already_running') {
        setSyncResult({ status: 'info', message: 'Synchronisation déjà en cours...' });
      }
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await api.get('/axonaut/status');
          setAxonautStatus(statusRes.data);
          if (statusRes.data?.last_sync?.status !== 'in_progress') {
            clearInterval(pollInterval);
            setSyncing(false);
            setSyncResult({
              status: statusRes.data.last_sync.status,
              companies_synced: statusRes.data.last_sync.companies_synced,
              invoices_synced: statusRes.data.last_sync.invoices_synced,
              ...statusRes.data.last_sync
            });
            // Refresh invoices
            const invoicesRes = await api.get('/axonaut/invoices?payment_status=unpaid').catch(() => ({ data: [] }));
            setInvoices(invoicesRes.data || []);
          }
        } catch (e) {
          console.error('Poll error:', e);
        }
      }, 3000);

      // Safety timeout
      setTimeout(() => {
        clearInterval(pollInterval);
        if (syncing) setSyncing(false);
      }, 180000);
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncing(false);
      setSyncResult({ status: 'error', errors: [error.response?.data?.detail || 'Erreur de synchronisation'] });
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Paramètres</h1>
        <p className="text-[#8C8F94] mt-1">Utilisateurs, intégrations et configuration</p>
      </div>

      {/* User Management */}
      <UserManagement />

      {/* Axonaut Integration */}
      <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#142915] flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#00E01A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg font-['Outfit']">Axonaut</h2>
              <p className="text-[#8C8F94] text-sm">Synchronisation clients & factures</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
            axonautStatus?.connected ? 'bg-[#142915] text-[#00E01A]' : 'bg-[#2A1515] text-[#E74C3C]'
          }`}>
            {axonautStatus?.connected ? 'Connecté' : 'Non configuré'}
          </div>
        </div>

        {/* Last sync info */}
        {axonautStatus?.last_sync && (
          <div className="bg-[#141617] rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[#8C8F94] text-sm">Dernière synchronisation</p>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                axonautStatus.last_sync.status === 'completed' ? 'bg-[#142915] text-[#00E01A]' : 'bg-[#2A1515] text-[#E74C3C]'
              }`}>
                {axonautStatus.last_sync.status === 'completed' ? 'Succès' : 'Erreur'}
              </span>
            </div>
            <p className="text-white text-sm">
              {new Date(axonautStatus.last_sync.started_at).toLocaleString('fr-FR')}
            </p>
            <div className="flex gap-4 mt-2">
              <span className="text-[#8C8F94] text-xs">{axonautStatus.last_sync.companies_synced} clients</span>
              <span className="text-[#8C8F94] text-xs">{axonautStatus.last_sync.invoices_synced} factures</span>
            </div>
          </div>
        )}

        {/* Sync button */}
        {isSuperAdmin() && (
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-6 py-3 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="axonaut-sync-btn"
          >
            {syncing ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                Synchronisation en cours...
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5"><path d="M21 2v6h-6M3 12a9 9 0 0115.36-6.36L21 8M3 22v-6h6M21 12a9 9 0 01-15.36 6.36L3 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Synchroniser Axonaut
              </>
            )}
          </button>
        )}

        {/* Sync result */}
        {syncResult && (
          <div className={`mt-4 rounded-lg p-4 ${
            syncResult.status === 'completed' ? 'bg-[#142915] border border-[#00E01A]/20' : 'bg-[#2A1515] border border-[#E74C3C]/20'
          }`}>
            <p className={`font-semibold text-sm mb-2 ${syncResult.status === 'completed' ? 'text-[#00E01A]' : 'text-[#E74C3C]'}`}>
              {syncResult.status === 'completed' ? 'Synchronisation terminée' : 'Erreur'}
            </p>
            {syncResult.status === 'completed' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="sync-result">
                <div><span className="text-[#8C8F94]">Clients sync:</span> <span className="text-white font-bold">{syncResult.companies_synced}</span></div>
                <div><span className="text-[#8C8F94]">Nouveaux:</span> <span className="text-white font-bold">{syncResult.companies_created}</span></div>
                <div><span className="text-[#8C8F94]">Factures sync:</span> <span className="text-white font-bold">{syncResult.invoices_synced}</span></div>
                <div><span className="text-[#8C8F94]">Nouvelles:</span> <span className="text-white font-bold">{syncResult.invoices_created}</span></div>
              </div>
            )}
            {syncResult.errors?.length > 0 && (
              <div className="mt-2 space-y-1">
                {syncResult.errors.map((err, i) => (
                  <p key={i} className="text-[#E74C3C] text-xs">{err}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gmail Integration */}
      <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#152A35] flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#3498DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 6l-10 7L2 6" stroke="#3498DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h2 className="text-white font-bold text-lg font-['Outfit']">Gmail</h2>
              <p className="text-[#8C8F94] text-sm">Synchronisation des prospects depuis vos emails</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
            gmailConnected ? 'bg-[#142915] text-[#00E01A]' : 'bg-[#2A1515] text-[#E74C3C]'
          }`} data-testid="gmail-status-badge">
            {gmailLoading ? 'Chargement...' : gmailConnected ? 'Connecte' : 'Non connecte'}
          </div>
        </div>

        <p className="text-[#8C8F94] text-sm mb-4">
          Connectez votre compte Gmail pour importer automatiquement les demandes de prospects depuis Formspree et riding-watt.com dans l'onglet Prospects Client.
        </p>

        {gmailConnected ? (
          <div className="flex items-center gap-3">
            <span className="text-[#00E01A] text-sm font-medium">Gmail est connecte et pret a synchroniser</span>
            <button
              onClick={handleGmailDisconnect}
              className="text-[#E74C3C] text-sm hover:underline"
              data-testid="gmail-disconnect-btn"
            >
              Deconnecter
            </button>
          </div>
        ) : (
          <button
            onClick={handleGmailConnect}
            className="flex items-center gap-2 bg-[#3498DB] hover:bg-[#2980B9] text-white font-semibold px-6 py-3 rounded-full transition-all shadow-[0_0_15px_rgba(52,152,219,0.3)]"
            data-testid="gmail-connect-settings-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Connecter Gmail
          </button>
        )}
      </div>

      {/* Recent unpaid invoices */}
      {invoices.length > 0 && (
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-6">
          <h3 className="text-white font-bold mb-4 font-['Outfit']">Factures impayées ({invoices.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2C3338]">
                  <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">N° Facture</th>
                  <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Client</th>
                  <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Date</th>
                  <th className="text-right p-3 text-[#8C8F94] text-xs font-semibold uppercase">Montant TTC</th>
                  <th className="text-right p-3 text-[#8C8F94] text-xs font-semibold uppercase">Reste dû</th>
                </tr>
              </thead>
              <tbody>
                {invoices.slice(0, 10).map((inv) => (
                  <tr key={inv.axonaut_id} className="border-b border-[#2C3338] last:border-0">
                    <td className="p-3 text-white font-mono text-sm">{inv.number}</td>
                    <td className="p-3 text-[#8C8F94] text-sm">{inv.company_name}</td>
                    <td className="p-3 text-[#8C8F94] text-sm">{inv.date ? new Date(inv.date).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className="p-3 text-right text-white font-semibold">{(inv.total_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€</td>
                    <td className="p-3 text-right text-[#E74C3C] font-semibold">{(inv.outstanding_amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout><Dashboard /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/suppliers" element={
        <ProtectedRoute>
          <AppLayout><Suppliers /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/suppliers/:supplierId" element={
        <ProtectedRoute>
          <AppLayout><SupplierDetail /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/products" element={
        <ProtectedRoute>
          <AppLayout><Products /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/products/:productId" element={
        <ProtectedRoute>
          <AppLayout><ProductDetail /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/clients" element={
        <ProtectedRoute>
          <AppLayout><Clients /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/clients/:clientId" element={
        <ProtectedRoute>
          <AppLayout><ClientPassport /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/inventory" element={
        <ProtectedRoute>
          <AppLayout><Inventory /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/sav" element={
        <ProtectedRoute>
          <AppLayout><SAV /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/sav/:ticketId" element={
        <ProtectedRoute>
          <AppLayout><SAVTicketDetail /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/loans" element={
        <ProtectedRoute>
          <AppLayout><Loans /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/prospecting" element={
        <ProtectedRoute>
          <AppLayout><Prospecting /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/exploitation" element={
        <ProtectedRoute>
          <AppLayout><ExploitationDashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/exploitation/clients" element={
        <ProtectedRoute>
          <AppLayout><ExploitationClients /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/exploitation/clients/:clientId" element={
        <ProtectedRoute>
          <AppLayout><ExploitationClientDetail /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/exploitation/site/:siteId" element={
        <ProtectedRoute>
          <AppLayout><ExploitationSiteDetail /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/exploitation/settings" element={
        <ProtectedRoute>
          <AppLayout><ExploitationSiteSettings /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/emails" element={
        <ProtectedRoute>
          <AppLayout><Emails /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/reseau" element={
        <ProtectedRoute>
          <AppLayout><Partners /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/marketing" element={
        <ProtectedRoute>
          <AppLayout><Marketing /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <AppLayout><Settings /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/configurateur" element={<ConfigurateurPage />} />
      <Route path="/configurateur/:brand" element={<ConfigurateurPage />} />
      <Route path="/guide-efoil" element={<ConfigurateurProfil />} />
      <Route path="/comparateur" element={
        <ProtectedRoute>
          <AppLayout><Comparateur /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/configurateur" element={
        <ProtectedRoute>
          <AppLayout><ConfigurateurAdminPage embeddedInCRM={true} /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#1D2327',
              color: '#FFFFFF',
              border: '1px solid #2C3338',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
